/* eslint-disable */
import React from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import SimpleStore from 'stores/simpleStore'
import { DiamondsMainSearchPage } from 'lib/pages/diamondsMainSearch'
import { GemstonesMainSearchPage } from 'lib/pages/gemstonesMainSearch'
import { DiamondPage } from 'lib/pages/diamond'
import { GemstonePage } from 'lib/pages/gemstone'
import { DiamondsPage } from 'lib/pages/diamonds'
import { GemstonesPage } from 'lib/pages/gemstones'
import { CollectionPage } from 'lib/pages/collection'
import { BespokePage } from 'lib/pages/bespoke'
import { S3_BUCKET } from 'constants/urls'
import { BespokeType } from 'constants/bespokeTypes'
import { Preloader } from 'lib/components/preloader'
import { CertificateViewer } from 'lib/components/modals/CertificateModal'
import { Roles } from 'constants/roles'
import logger from 'utils/logger'
import { throwIfAborted } from 'utils/abortSignal'
import { Currency } from 'types/currency'
import { DEFAULT_STORE_CURRENCY, getSupportedCurrencies, getCurrencySymbol } from 'constants/currencies'
import { CurrencyInfo } from 'types/interfaces'
import { AppProvider } from 'lib/context/AppContext'
import i18nInstance, { applyUbDsLocale, getEffectiveLocale } from 'i18n'
import { resolveTypeLabels } from 'utils/typeLabels'
import './variables.css'
// Normalized (rem -> px) snapshot of '@unbridaled/search-sdk/main.shopify.css' — see
// SHO-298 / scripts/sync-sdk-styles.js. Imported instead of the raw package stylesheet
// so the widget's sizing isn't at the mercy of the host theme's root font-size.
import  './sdk.css'
import './App.css'
interface AppState {
  appConfig: any | null
  loaded: boolean
  enabledDiamondGemTypes: string[]
  enabledSourceTypes: string[]
  merchantTypeLabels: Record<string, string>
  isAdmin: boolean
  currencyInfo: CurrencyInfo
}

class App extends React.Component<{}, AppState> {
  constructor(props: {}) {
    super(props)

    this.state = {
      appConfig: null,
      loaded: false,
      enabledDiamondGemTypes: [],
      enabledSourceTypes: [],
      merchantTypeLabels: {},
      isAdmin: false,
      currencyInfo: {
        currency: DEFAULT_STORE_CURRENCY,
        currencyRate: 1,
        currencies: []
      }
    }

    // Currency symbols are locale-dependent (e.g. CAD renders as "CA$" in en-US but
    // "$" in fr-CA). fetchCurrencyInfo() below can run before ubDS.effectiveLocale has
    // resolved (pollUbDsLocale only starts once it settles), so re-derive the symbols
    // whenever i18next's resolved language actually changes.
    i18nInstance.on('languageChanged', this.refreshCurrencySymbols)

    this.initializeApp()
  }

  async initializeApp() {
    try {
      await Promise.all([
        this.loadMainCss(),
        this.fetchCurrencyInfo(),
        this.loadAppConfig()
      ])
      this.setState({ loaded: true })
    } catch (error) {
      logger.error('Error initializing app:', error)
      this.setState({ loaded: true })
    }
    this.pollUbDsLocale()
  }

  // window.ubDS.effectiveLocale is set by the theme extension's own async /app_version
  // fetch, which can resolve after this module has already mounted, so poll briefly.
  pollUbDsLocale(attempt = 0) {
    applyUbDsLocale()
    if (attempt < 10) {
      setTimeout(() => this.pollUbDsLocale(attempt + 1), 300)
    }
  }

  refreshCurrencySymbols = (): void => {
    this.setState(({ currencyInfo }) => ({
      currencyInfo: {
        ...currencyInfo,
        currencies: currencyInfo.currencies.map(currency => ({
          ...currency,
          symbol: getCurrencySymbol(getEffectiveLocale(), currency.code)
        }))
      }
    }))
  }

  async checkIsAdmin() {
    try {
      const USER_COOKIE_NAME = 'UNBRIDALED_USER'
      const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
      const simpleStore = new SimpleStore()
      const resp = await simpleStore.ajax('POST', 'check_is_admin', JSON.stringify({ userData }))
      this.setState({
        isAdmin: resp?.result
      })
    } catch (e) {
      logger.info(e)
    }
  }

  async loadAppConfig() {
    try {
      const simpleStore = new SimpleStore()
      const appConfig = await simpleStore.ajax('GET', 'app_config')
      if (appConfig?.source_types === undefined) {
        return null
      }
      const enabledDiamondGemTypes: string[] = []
      const enabledSourceTypes: string[] = []
      const merchantTypeLabels: Record<string, string> = {}
      for (const type of appConfig.source_types) {
        if (type.name === 'mined' && type.enabled) {
          enabledDiamondGemTypes.push('mined')
        } else if (type.name === 'lab grown' && type.enabled) {
          enabledDiamondGemTypes.push('lab grown')
        }

        if (type.enabled) {
          enabledSourceTypes.push(type.name)
        }
        merchantTypeLabels[type.name] = type.label
      }
      this.setState({
        appConfig,
        enabledDiamondGemTypes,
        enabledSourceTypes,
        merchantTypeLabels
      })
      if (window.location.pathname.includes('merchant')) {
        await this.checkIsAdmin()
      }
    } catch (e) {
      logger.info(e)
    }
  }

  fetchCurrencyInfo = async (): Promise<void> => {
    try {
      let currencyCode = window.Shopify?.currency?.active
      const simpleStore = new SimpleStore()
      const currencyData = await simpleStore.ajax('GET', 'shop_currency')
      if (currencyData?.sessionExpired && !currencyCode) {
        return
      }
      if (!currencyCode && currencyData?.currency) {
        currencyCode = currencyData.currency
      }
      const data = await simpleStore.ajax('GET', 'defaults/exchange_rates')
      if (data?.sessionExpired) {
        return
      }
      const exchangeRates = data?.payload
      if (!exchangeRates || typeof exchangeRates !== 'object') {
        return
      }
      const currencies = getSupportedCurrencies().map(currency => ({
        ...currency,
        rate: exchangeRates[currency.code]
      })).sort(a => (a.code === Currency.USD ? -1 : 1))
      if (exchangeRates[currencyCode] !== undefined) {
        this.setState({
          currencyInfo: {
            currencyRate: exchangeRates[currencyCode],
            currency: currencyCode,
            currencies
          }
        })
      }
    } catch (e) {
      logger.info(e)
    }
  }

  loadMainCss(): Promise<void> {
    // Skip in local dev: this always fetches the deployed S3 main.css and appends it to
    // <head> after webpack's own style-loader-injected <style> tags, so at equal
    // specificity it wins the cascade — silently overriding whatever CSS changes are
    // actually running locally (dev server or a local shopify-v1 build) with whatever's
    // currently deployed to S3, which may be stale or unrelated. Runs for every other
    // tier (development/staging/production) since those are real deployed builds that
    // are meant to pull the matching S3 bundle.
    if (process.env.APP_ENV === 'local') {
      return Promise.resolve()
    }
    return new Promise((resolve, reject) => {
      const url = `https://${S3_BUCKET}.s3.amazonaws.com/static/css/main.css`
      const head = document.head || document.getElementsByTagName('head')[0]
      const link = document.createElement('link')

      link.type = 'text/css'
      link.rel = 'stylesheet'
      link.href = url
      
      link.onload = () => resolve()
      link.onerror = () => reject(new Error('Failed to load CSS'))
      
      head.appendChild(link)
    })
  }

  override render() {
    const { appConfig, enabledDiamondGemTypes, enabledSourceTypes, merchantTypeLabels, isAdmin, loaded } =
      this.state
    const typeLabels = resolveTypeLabels(merchantTypeLabels)
    const simpleStore = new SimpleStore()
    const USER_COOKIE_NAME = 'UNBRIDALED_USER'
    const diamondStore = {
      getDiamond: async (
        searchParams: { [key: string]: any },
        searchType: string,
        role: string = Roles.Customer
      ) => {
        const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
        let data = await simpleStore.ajax(
          'POST',
          'get_diamonds',
          JSON.stringify({ searchParams, searchType, userData, role })
        )
        if (data?.sessionExpired) {
          data = await simpleStore.ajax(
            'POST',
            'get_diamonds',
            JSON.stringify({ searchParams, searchType, userData: {}, role })
          )
        }
        if (data?.success) {
          return data.payload
        } else {
          return false
        }
      },
      getDefaultCollections: async () => {
        const data = await simpleStore.ajax('GET', 'bespoke/default_collections')
        if (data?.success) {
          return data.payload
        } else {
          return false
        }
      },
      getRatios: async () => {
        const data = await simpleStore.ajax('GET', 'bespoke/ratios')
        if (data?.success) {
          return data.payload
        } else {
          return false
        }
      },
      getExchangeRates: async () => {
        const data = await simpleStore.ajax('GET', 'defaults/exchange_rates')
        if (data?.success) {
          return data.payload
        } else {
          return false
        }
      },
      getDiamondWeights: async (params: { [key: string]: any }) => {
        const data = await simpleStore.ajax(
          'POST',
          `diamond_weights_without_prices/${params.weightMin}/${params.weightMax}`
        )
        if (data?.success) {
          return data?.payload?.weights || []
        } else {
          return false
        }
      },
      getDiamondsByMerchantTemplates: async (params: { [key: string]: any }, role: string) => {
        const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
        let resp = await simpleStore.ajax(
          'POST',
          `bespoke/ds/${role}`,
          JSON.stringify({ params, userData })
        )
        if (resp?.sessionExpired) {
          resp = await simpleStore.ajax(
            'POST',
            `bespoke/ds/${role}`,
            JSON.stringify({ params, userData: {} })
          )
        }
        if (Array.isArray(resp)) {
          return resp
        }
        if (resp?.success) {
          return Array.isArray(resp.payload) ? resp.payload : null
        }
        return null
      },
      getStatus: (sku: string) => {
        let skuNum = 0
        let skuAt = sku.length
        while (skuAt > 0) {
          skuNum += sku.charCodeAt(skuAt)
          skuAt -= 1
        }
        return new Promise(resolve => {
          setTimeout(() => {
            resolve({ isAvailable: Boolean(skuNum % 2) })
          }, 1200)
        })
      },
      runFilter: async (
        request: { [key: string]: any },
        searchType: string,
        signal?: AbortSignal
      ) => {
        const apiURL = `ds/${searchType}`
        const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
        try {
          let resp = await simpleStore.ajax(
            'POST',
            apiURL,
            JSON.stringify({ request, userData }),
            signal
          )
          throwIfAborted(signal)
          if (resp?.sessionExpired) {
            resp = await simpleStore.ajax(
              'POST',
              apiURL,
              JSON.stringify({ request, userData: {} }),
              signal
            )
            throwIfAborted(signal)
          }
          return resp?.payload
        } catch (e) {
          const error = e as Error
          if (error.name === 'AbortError') {
            throw error
          }
          logger.info(e)
          return null
        }
      },
      startOTP: async (email: string) =>
        await simpleStore.ajax('POST', `ds/start_otp`, JSON.stringify({ email })),
      validateOTP: async (methodId: string, code: string) =>
        await simpleStore.ajax('GET', `ds/validate_otp/?method_id=${methodId}&code=${code}`),
      getShopName: async () => await simpleStore.ajax('GET', 'shop_name')
    }

    const diamondsSearchPage = '/apps/ub/diamonds'
    const singleDiamondRetailPage = '/apps/ub/diamond/:id'
    const adminLoginPage = '/apps/ub/diamonds/admin'
    const singleDiamondWholesalePage = '/apps/ub/merchant/diamond/:id'
    const diamondsPage = '/apps/ub/curated_diamonds/:base64_hash'
    const gemstonesPage = '/apps/ub/gemstones/:gemstone'
    const gemstonesSearchPage = '/apps/ub/gemstones'
    const singleGemstoneRetailPage = '/apps/ub/gemstone/:id'
    const singleGemstoneWholesalePage = '/apps/ub/merchant/gemstone/:id'
    const curatedGemstonesPage = '/apps/ub/curated_gemstones/:base64_hash'
    const collectionPage = '/apps/ub/diamond_collection/:id'
    const bespokeSolitairePage = '/apps/ub/diamonds-ai'
    const bespokePairPage = '/apps/ub/pairs-ai'

    const contextValue = {
      diamondStore,
      appConfig,
      enabledDiamondGemTypes,
      enabledSourceTypes,
      typeLabels,
      currencyInfo: this.state.currencyInfo,
      isAdmin,
      loaded
    }

    return (
      <main className="main">
        <AppProvider value={contextValue}>
          <Switch>
            <Route
              path={collectionPage}
              render={props => (
                <React.StrictMode>
                  <CollectionPage {...props} />
                </React.StrictMode>
              )}
            />
            <Route
              path={adminLoginPage}
              render={props => (
                <React.StrictMode>
                  <Redirect to={`${diamondsSearchPage}?admin=true`} />
                </React.StrictMode>
              )}
            />
            <Route
              path={diamondsPage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <DiamondsPage {...props} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              path={singleDiamondRetailPage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <DiamondPage {...props} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              path={singleDiamondWholesalePage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <>
                      {isAdmin ? (
                        <DiamondPage {...props} />
                      ) : (
                        <Redirect
                          to={singleDiamondRetailPage.replace(':id', props.match.params.id)}
                        />
                      )}
                    </>
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              path={bespokeSolitairePage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <BespokePage {...props} bespokeType={BespokeType.Single} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              path={bespokePairPage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <BespokePage {...props} bespokeType={BespokeType.Pair} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              exact
              path={diamondsSearchPage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <DiamondsMainSearchPage {...props} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              exact
              path={gemstonesSearchPage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <GemstonesMainSearchPage {...props} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              exact
              path={gemstonesPage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <GemstonesMainSearchPage {...props} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              path={curatedGemstonesPage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <GemstonesPage {...props} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              path={singleGemstoneRetailPage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <GemstonePage {...props} />
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Route
              path={singleGemstoneWholesalePage}
              render={props => (
                <React.StrictMode>
                  {loaded ? (
                    <>
                      {isAdmin ? (
                        <GemstonePage {...props} />
                      ) : (
                        <Redirect
                          to={singleGemstoneRetailPage.replace(':id', props.match.params.id)}
                        />
                      )}
                    </>
                  ) : (
                    <Preloader preloaderClass="unbridaled-full-page-preloader" />
                  )}
                </React.StrictMode>
              )}
            />
            <Redirect to={diamondsSearchPage} />
          </Switch>
          <CertificateViewer />
        </AppProvider>
      </main>
    )
  }
}

export default App
